import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Users, TrendingUp, Star, Search } from 'lucide-react';
import AgentAIAnalysis from '../../components/agency/AgentAIAnalysis';
import EmptyState from '../../components/shared/EmptyState';

const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (trend === 'down') return <TrendingUp className="w-4 h-4 text-red-400 rotate-180" />;
    return <span className="w-4 h-4 text-muted-foreground">—</span>;
};

export default function AgentRankingList({ agents, searchQuery, setSearchQuery, sortBy, setSortBy }) {
    const filteredAgents = agents
        .filter(a => a.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a, b) => {
            switch (sortBy) {
                case 'mandates': return (b.mandates_active || 0) - (a.mandates_active || 0);
                case 'rating': return (b.avg_rating || 0) - (a.avg_rating || 0);
                case 'revenue': return (b.revenue_month || 0) - (a.revenue_month || 0);
                default: return (b.mandates_active || 0) - (a.mandates_active || 0);
            }
        });

    return (
        <Card className="bg-slate-900 border-border">
            <CardHeader className="p-3 sm:p-6">
                <div className="flex flex-col gap-3">
                    <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                        <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF914D]" />
                        Classement agents
                    </CardTitle>
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground w-3.5 h-3.5" />
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Rechercher..."
                                className="pl-8 h-8 text-xs bg-card border-border text-white"
                            />
                        </div>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="bg-card border border-border text-white rounded-md px-2 py-1 text-xs h-8"
                        >
                            <option value="mandates" className="bg-slate-900">Mandats</option>
                            <option value="rating" className="bg-slate-900">Note</option>
                            <option value="revenue" className="bg-slate-900">CA</option>
                        </select>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0 sm:p-6 sm:pt-0">
                <div className="space-y-1 sm:space-y-3">
                    {filteredAgents.map((agent, idx) => {
                        const aiRef = React.createRef();
                        return (
                        <motion.div
                            key={agent.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            className="p-3 sm:p-4 sm:rounded-xl bg-card hover:bg-muted transition-colors border-b border-border/30 sm:border-0"
                        >
                            <div className="flex items-center gap-2.5 sm:gap-3">
                                {/* Rank */}
                                <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-[10px] sm:text-sm shrink-0 ${
                                    idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                                    idx === 1 ? 'bg-gray-400/20 text-foreground' :
                                    idx === 2 ? 'bg-amber-700/20 text-amber-500' :
                                    'bg-muted text-muted-foreground'
                                }`}>
                                    {idx + 1}
                                </div>
                                {/* Avatar */}
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-bold text-xs sm:text-sm shrink-0">
                                    {agent.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                {/* Name + stats */}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <span className="font-medium text-white text-xs sm:text-sm truncate">{agent.name}</span>
                                        {getTrendIcon(agent.trend)}
                                    </div>
                                    <div className="flex items-center gap-2 lg:hidden mt-0.5">
                                        <span className="text-[10px] text-muted-foreground">{agent.mandates_active} mandats</span>
                                        <span className="text-[10px] text-yellow-400 flex items-center gap-0.5">
                                            <Star className="w-2.5 h-2.5" />{agent.avg_rating?.toFixed(1)}
                                        </span>
                                        <span className="text-[10px] text-green-400">{(agent.revenue_month || 0).toLocaleString()}€</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground hidden lg:block truncate">{agent.email}</p>
                                </div>
                                {/* Desktop stats */}
                                <div className="hidden lg:flex items-center gap-5 shrink-0">
                                    <div className="text-center min-w-[60px]">
                                        <p className="text-base font-bold text-white">{agent.mandates_active}</p>
                                        <p className="text-[10px] text-muted-foreground">Mandats</p>
                                    </div>
                                    <div className="text-center min-w-[60px]">
                                        <p className="text-base font-bold text-white flex items-center justify-center gap-1">
                                            <Star className="w-3.5 h-3.5 text-yellow-400" />{agent.avg_rating?.toFixed(1)}
                                        </p>
                                        <p className="text-[10px] text-muted-foreground">Note</p>
                                    </div>
                                    <div className="text-center min-w-[70px]">
                                        <p className="text-base font-bold text-green-400">{(agent.revenue_month || 0).toLocaleString()}€</p>
                                        <p className="text-[10px] text-muted-foreground">CA mois</p>
                                    </div>
                                    <div>
                                        <AgentAIAnalysis agentId={agent.id} agentName={agent.name} />
                                    </div>
                                </div>
                            </div>
                            <div className="lg:hidden mt-2 pl-[58px]">
                                <AgentAIAnalysis agentId={agent.id} agentName={agent.name} />
                            </div>
                        </motion.div>
                        );
                    })}
                </div>
                {filteredAgents.length === 0 && (
                    <EmptyState
                        icon={<Users />}
                        title="Aucun agent trouvé"
                        description={searchQuery ? "Modifiez votre recherche." : "Invitez vos agents depuis les paramètres."}
                        compact
                        testId="agents-empty-state"
                    />
                )}
            </CardContent>
        </Card>
    );
}
